import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import DataTable from 'react-data-table-component';

import {
  Modal,
  Form,
  Row,
  Col,
  Button,
  InputGroup,
  Container,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { FaEdit, FaTrashAlt, FaFileExcel, FaBoxOpen } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './AdminLayout.css';
import BASE_URL from '../config';

// Axios instance with interceptor for authentication
const authAxios = axios.create({
  baseURL: BASE_URL,
});
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Map backend role values to nice labels
const ROLE_LABELS = {
  logistics_manager: 'Logistics Manager',
  driver: 'Driver',
  accountant: 'Accountant',
  cashier: 'Cashier',
  branch_manager: 'Branch Manager',
  sales: 'Sales',
};

// Fixed roles list (no admin)
const FIXED_ROLES = Object.entries(ROLE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

// Admin User Creation/Edit Modal (Unchanged for brevity)
const AdminUserCreation = ({
  show,
  onClose,
  onUserCreated,
  userToEdit,
  allShowrooms,
  showMessageModal,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    role: null,
    showrooms: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userToEdit) {
      const selectedShowrooms = (userToEdit.showroom_id || [])
        .map((id) => {
          const showroom = allShowrooms.find((s) => s.id === id);
          return showroom ? { value: showroom.id, label: showroom.name } : null;
        })
        .filter(Boolean);

      const selectedRole =
        FIXED_ROLES.find((r) => r.value === userToEdit.role) || null;

      setFormData({
        name: userToEdit.name || '',
        phone: userToEdit.phone || '',
        email: userToEdit.email || '',
        password: '',
        role: selectedRole,
        showrooms: selectedShowrooms,
      });
    } else {
      setFormData({
        name: '',
        phone: '',
        email: '',
        password: '',
        role: null,
        showrooms: [],
      });
    }
    setErrors({});
  }, [userToEdit, allShowrooms, show]); // Added 'show' to reset form when modal opens

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSelectChange = (field) => (selectedOption) => {
    setFormData((prev) => ({ ...prev, [field]: selectedOption }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const showroomIds = (formData.showrooms || []).map((s) => s.value);

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      showroom_id: showroomIds,
      is_active: true,
      online: false,
      role: formData.role?.value || '',
    };

    if (formData.password) payload.password = formData.password;

    try {
      if (userToEdit) {
        await authAxios.put(`/users/${userToEdit.id}`, payload);
        showMessageModal('Success', 'User updated successfully! 🎉');
      } else {
        await authAxios.post('/users', payload);
        showMessageModal('Success', 'User created successfully! 🎉');
      }
      onUserCreated();
      onClose();
    } catch (err) {
      console.error('❌ Error submitting user form:', err);
      if (err.response) setErrors(err.response.data.errors || {});
      showMessageModal(
        'Error',
        err.response?.data?.message || err.message || 'An error occurred.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          {userToEdit ? 'Edit User' : 'Create New User'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group controlId='name' className='mb-3'>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type='text'
                  name='name'
                  value={formData.name}
                  onChange={handleChange}
                  placeholder='Enter name'
                  isInvalid={!!errors.name}
                  required
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.name?.[0]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId='phone' className='mb-3'>
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type='text'
                  name='phone'
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder='Enter Phone Number'
                  isInvalid={!!errors.phone}
                  required
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.phone?.[0]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId='email' className='mb-3'>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  placeholder='Enter Email'
                  isInvalid={!!errors.email}
                  required
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.email?.[0]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId='password' className='mb-3'>
                <Form.Label>
                  Password{' '}
                  {userToEdit && (
                    <small className='text-muted'>
                      (Leave blank to keep existing)
                    </small>
                  )}
                </Form.Label>
                <Form.Control
                  type='password'
                  name='password'
                  value={formData.password}
                  onChange={handleChange}
                  placeholder='Enter a password'
                  isInvalid={!!errors.password}
                  required={!userToEdit}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.password?.[0]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group controlId='role' className='mb-3'>
                <Form.Label>Role</Form.Label>
                <Select
                  options={FIXED_ROLES}
                  onChange={handleSelectChange('role')}
                  placeholder='Select a role'
                  value={formData.role}
                  isClearable
                />
                {errors.role && (
                  <div
                    className='text-danger mt-1'
                    style={{ fontSize: '0.875em' }}
                  >
                    {errors.role[0]}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId='showrooms' className='mb-3'>
                <Form.Label>Showrooms</Form.Label>
                <Select
                  isMulti
                  options={allShowrooms.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  onChange={handleSelectChange('showrooms')}
                  value={formData.showrooms}
                  isInvalid={!!errors.showroom_id}
                />
                {errors.showroom_id && (
                  <div
                    className='text-danger mt-1'
                    style={{ fontSize: '0.875em' }}
                  >
                    {errors.showroom_id[0]}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Button
            variant='primary'
            style={{ width: '100%' }}
            type='submit'
            disabled={loading}
          >
            {loading ? 'Submitting...' : userToEdit ? 'Update' : 'Create'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// Main Admin Users Component
const AdminUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [allShowrooms, setAllShowrooms] = useState([]);
  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const isFetchingRef = useRef(false);
  const intersectionObserverRef = useRef(null);
  const lastRowRef = useRef(null);

  const showMessageModal = (title, body) => {
    setMessageModal({ show: true, title, body });
  };

  const fetchUsers = async (page, rowsPerPage, append = false) => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;

    // Set loading state based on whether we're appending or replacing
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const params = {
        per_page: rowsPerPage,
      };

      const res = await authAxios.get(`/users?page=${page}`, { params });
      const responseData = res.data?.data;
      const usersData = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
        ? responseData
        : [];

      // Filter out admins
      const filteredUsers = usersData.filter(
        (u) => u.role?.toLowerCase() !== 'admin'
      );

      // Update pagination metadata from Laravel pagination response
      if (responseData?.total !== undefined) {
        setTotalRows(responseData.total);
      } else if (responseData?.last_page && responseData?.per_page) {
        const estimatedTotal = responseData.last_page * responseData.per_page;
        setTotalRows(estimatedTotal);
      } else {
        setTotalRows(filteredUsers.length);
      }

      // Update last page and hasMore state
      if (responseData?.last_page !== undefined) {
        setLastPage(responseData.last_page);
        setHasMore(page < responseData.last_page);
      }

      // Append or replace users based on the append flag
      if (append) {
        setAllUsers((prevUsers) => [...prevUsers, ...filteredUsers]);
      } else {
        setAllUsers(filteredUsers);
      }

      // Update current page
      setCurrentPage(page);
    } catch (err) {
      console.error('❌ Failed to fetch users:', err);
      if (!append) {
        setAllUsers([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  const fetchShowrooms = async () => {
    try {
      const showroomsRes = await authAxios.get(`/showrooms`);
      const showroomsData =
        showroomsRes.data?.data?.data || showroomsRes.data?.data || [];
      setAllShowrooms(showroomsData);
    } catch (err) {
      console.error('❌ Error fetching showrooms:', err);
    }
  };

  useEffect(() => {
    fetchShowrooms();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setAllUsers([]); // Clear users on initial load
      setCurrentPage(1);
      setHasMore(true);
      await fetchUsers(1, perPage, false);
    };
    fetchData();
  }, []);

  // --- Frontend Filtering & Search Logic ---
  const filteredAndSearchedUsers = useMemo(() => {
    let filtered = allUsers;

    if (search) {
      filtered = filtered.filter(
        (u) =>
          u.name?.toLowerCase().includes(search.toLowerCase()) ||
          u.email?.toLowerCase().includes(search.toLowerCase()) ||
          u.phone?.includes(search)
      );
    }
    if (selectedRole && selectedRole.value) {
      const sel = String(selectedRole.value).toLowerCase();
      filtered = filtered.filter(
        (u) => String(u.role || '').toLowerCase() === sel
      );
    }

    return filtered;
  }, [allUsers, selectedRole, search]);

  // Load more users for infinite scroll - using useCallback to stabilize the function
  const loadMoreUsers = useCallback(async () => {
    if (!hasMore || isLoadingMore || isFetchingRef.current) {
      return;
    }

    const nextPage = currentPage + 1;
    await fetchUsers(nextPage, perPage, true); // append = true
  }, [currentPage, hasMore, isLoadingMore, perPage]);

  // Effect to set up Intersection Observer for infinite scroll using last row
  useEffect(() => {
    // Clean up previous observer
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }

    let mutationObserver = null;
    const mutationObserverRef = { current: null };

    const setupIntersectionObserver = () => {
      // Find the DataTable body
      const tableBody = document.querySelector('.rdt_TableBody');
      if (!tableBody) {
        setTimeout(setupIntersectionObserver, 200);
        return;
      }

      // Find all rows in the table
      const rows = tableBody.querySelectorAll('.rdt_TableRow');
      if (rows.length === 0) {
        setTimeout(setupIntersectionObserver, 200);
        return;
      }

      // Disconnect previous observer if exists
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }

      // Get the last row (using page number and data index)
      const lastRow = rows[rows.length - 1];
      if (!lastRow) return;

      // Add ID to last row for identification (using page number and row index)
      const lastRowIndex = rows.length - 1;
      const rowId = `last-row-page-${currentPage}-index-${lastRowIndex}`;
      lastRow.id = rowId;
      lastRow.setAttribute('data-page', currentPage);
      lastRow.setAttribute('data-index', lastRowIndex);

      // Store reference to last row
      lastRowRef.current = lastRow;

      // Create Intersection Observer
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // When the last row becomes visible (or partially visible)
            if (entry.isIntersecting) {
              console.log(
                'Last row is intersecting - checking if should load more:',
                {
                  hasMore,
                  isLoadingMore,
                  isFetching: isFetchingRef.current,
                  currentPage,
                  totalRows: rows.length,
                  rowId: entry.target.id,
                }
              );

              // Load more if conditions are met
              if (hasMore && !isLoadingMore && !isFetchingRef.current) {
                console.log(
                  'Triggering loadMoreUsers via Intersection Observer'
                );
                loadMoreUsers();
              }
            }
          });
        },
        {
          // Trigger when last row is 100px from viewport
          root: tableBody,
          rootMargin: '100px',
          threshold: 0.1, // Trigger when 10% of the row is visible
        }
      );

      // Observe the last row
      observer.observe(lastRow);
      intersectionObserverRef.current = observer;

      console.log('Intersection Observer set up for last row:', {
        rowId,
        rowIndex: rows.length - 1,
        totalRows: rows.length,
        currentPage,
        hasMore,
        element: lastRow,
      });

      // Set up MutationObserver to watch for new rows being added
      if (!mutationObserverRef.current) {
        mutationObserver = new MutationObserver(() => {
          // When rows are added, update the intersection observer
          const newRows = tableBody.querySelectorAll('.rdt_TableRow');
          if (newRows.length > rows.length) {
            console.log('New rows detected, updating intersection observer');
            // Disconnect and reconnect to new last row
            if (intersectionObserverRef.current) {
              intersectionObserverRef.current.disconnect();
            }
            setTimeout(setupIntersectionObserver, 100);
          }
        });

        mutationObserver.observe(tableBody, {
          childList: true,
          subtree: false,
        });
        mutationObserverRef.current = mutationObserver;
      }
    };

    // Try to set up observer immediately
    setupIntersectionObserver();

    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
        intersectionObserverRef.current = null;
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
      lastRowRef.current = null;
    };
  }, [
    hasMore,
    isLoadingMore,
    loadMoreUsers,
    filteredAndSearchedUsers.length,
    currentPage,
  ]);

  const handleOpenCreateModal = () => {
    setEditingUser(null);
    setShowModal(true);
  };
  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await authAxios.delete(`/users/${userId}`);
        showMessageModal('Success', 'User deleted successfully! 🗑️');
        // Reset and reload users from the beginning
        setAllUsers([]);
        setCurrentPage(1);
        setHasMore(true);
        await fetchUsers(1, perPage, false);
      } catch (err) {
        console.error('Failed to delete user:', err.response?.data || err);
        showMessageModal(
          'Error',
          'Error deleting user: ' + (err.response?.data?.message || err.message)
        );
      }
    }
  };

  // --- FIX 3: Export to Excel uses ALL non-admin users ---
  const handleExportToExcel = () => {
    if (!allUsers || allUsers.length === 0) {
      showMessageModal('Info', 'No user data to export');
      return;
    }

    // Export ALL users (from allUsers state), regardless of current search/filter/page
    const exportData = allUsers.map((u, idx) => ({
      'S.No': idx + 1,
      Name: u.name,
      Email: u.email || 'N/A',
      Phone: u.phone,
      Role: ROLE_LABELS[u.role] || u.role || 'N/A',
      Showrooms: (u.showroom_id || [])
        .map((id) => {
          const showroom = allShowrooms.find((s) => s.id === id);
          return showroom ? showroom.name : 'Unknown Showroom';
        })
        .join(', '),
      Status: u.is_active ? 'Active' : 'Inactive',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `users_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  // Custom Loading Component for DataTable
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>Loading Users...</span>
    </div>
  );

  // Custom No Data Component for DataTable
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Users Found</h5>
      <p className='mb-0'>
        There are currently no users in this list or matching your search
        criteria.
      </p>
    </div>
  );

  const columns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '3vw',
      compact: true,
      grow: 0,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '8vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Email',
      selector: (row) => row.email || 'N/A',
      sortable: true,
      minWidth: '10vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Phone',
      selector: (row) => row.phone,
      sortable: true,
      minWidth: '6vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Role',
      selector: (row) => ROLE_LABELS[row.role] || row.role || 'N/A',
      sortable: true,
      minWidth: '6vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Showrooms',
      selector: (row) =>
        Array.isArray(row.showroom_id)
          ? row.showroom_id
              .map((id) => {
                const showroom = allShowrooms.find((s) => s.id === id);
                return showroom ? showroom.name : 'Unknown';
              })
              .join(', ')
          : 'N/A',
      sortable: true,
      minWidth: '10vw',
      wrap: true,
      grow: 2,
      style: {
        wordBreak: 'break-word',
      },
    },
    {
      name: 'Status',
      selector: (row) => (row.is_active ? 'Active' : 'Inactive'),
      sortable: true,
      minWidth: '5vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Actions',
      center: true,
      cell: (row) => {
        const buttons = [
          <Button
            key='edit'
            size='sm'
            variant='warning'
            onClick={() => handleOpenEditModal(row)}
            title='Edit user'
          >
            <FaEdit />
          </Button>,
          <Button
            key='delete'
            size='sm'
            variant='danger'
            onClick={() => handleDeleteUser(row.id)}
            title='Delete user'
          >
            <FaTrashAlt />
          </Button>,
        ];

        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            {buttons.map((button) =>
              React.cloneElement(button, {
                style: {
                  ...button.props.style,
                  marginTop: '4px',
                  marginBottom: '4px',
                },
              })
            )}
          </div>
        );
      },
      minWidth: '6vw',
      grow: 0,
    },
  ];

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <h3 style={{ textAlign: 'center' }}>Users</h3>
        <div className='row justify-content-between align-items-center mt-3 mb-3'>
          <div className='col-md-9 col-lg-9 col-sm-12 align-items-center py-3 gap-2'>
            <Row className='g-2'>
              <Col xs='auto'>
                <InputGroup style={{ width: '300px' }}>
                  <InputGroup.Text>
                    <i className='fa fa-search'></i>
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search Users'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col xs='auto'>
                <div style={{ width: '200px' }}>
                  <Select
                    options={[
                      { value: null, label: 'All Roles' },
                      ...FIXED_ROLES,
                    ]}
                    onChange={(selectedOption) =>
                      setSelectedRole(selectedOption)
                    }
                    placeholder='Filter by Role'
                    value={selectedRole}
                    isClearable
                  />
                </div>
              </Col>
            </Row>
          </div>
          <div className='col-md-3 col-lg-3 col-sm-12 text-end'>
            <Button
              size='sm'
              variant='primary'
              onClick={handleOpenCreateModal}
              style={{ marginRight: '2px' }}
            >
              <i className='fa fa-plus'></i> New User
            </Button>
            <Button size='sm' variant='success' onClick={handleExportToExcel}>
              <FaFileExcel /> Export to Excel
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <div style={{ maxHeight: '54vh', position: 'relative' }}>
          {/* Count Display */}
          {filteredAndSearchedUsers.length > 0 && (
            <div
              className='text-end p-2'
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
              }}
            >
              <small className='text-muted'>
                Showing <strong>{1}</strong> to{' '}
                <strong>{filteredAndSearchedUsers.length}</strong> out of{' '}
                <strong>{totalRows}</strong> users
              </small>
            </div>
          )}
          <DataTable
            columns={columns}
            data={filteredAndSearchedUsers}
            pagination={false}
            highlightOnHover
            striped
            dense
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight='50vh'
            noTableHead={false}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: '2px',
                  borderBottomColor: '#dee2e6',
                },
              },
              headCells: {
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              },
              cells: {
                style: {
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              },
            }}
            progressPending={loading && !isLoadingMore}
            progressComponent={CustomLoadingIndicator}
            noDataComponent={CustomNoDataComponent}
          />
          {/* Infinite scroll loading indicator */}
          {isLoadingMore && (
            <div className='text-center p-3'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span>Loading more users...</span>
            </div>
          )}
          {!hasMore && filteredAndSearchedUsers.length > 0 && (
            <div className='text-center p-3 text-muted'>
              <small>No more users to load</small>
            </div>
          )}
        </div>

        <AdminUserCreation
          show={showModal}
          onClose={handleCloseModal}
          onUserCreated={async () => {
            // Reset and reload users from the beginning
            setAllUsers([]);
            setCurrentPage(1);
            setHasMore(true);
            await fetchUsers(1, perPage, false);
          }}
          userToEdit={editingUser}
          allShowrooms={allShowrooms}
          showMessageModal={showMessageModal}
        />
        {/* Message Modal */}
        <Modal
          show={messageModal.show}
          onHide={() => setMessageModal({ show: false, title: '', body: '' })}
          centered
          size='md'
          className='text-center'
        >
          <Modal.Body
            className='py-4'
            style={{
              backgroundColor: '#fff',
              color: '#1f1f1f',
              borderRadius: '15px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div className='mb-3'>
              {messageModal.title === 'Success' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='green'
                  className='bi bi-check-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z' />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='red'
                  className='bi bi-x-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z' />
                </svg>
              )}
            </div>
            <h5 className='mb-2'>{messageModal.title}</h5>
            <p>{messageModal.body}</p>
            <Button
              variant={messageModal.title === 'Success' ? 'success' : 'danger'}
              style={{ width: '100%' }}
              onClick={() =>
                setMessageModal({ show: false, title: '', body: '' })
              }
            >
              Close
            </Button>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminUsers;
